import React, { useState } from "react";
import { Col, Divider, Row, Tag, Typography, Grid } from "antd";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Definition from "../../../components/Definition";

const { useBreakpoint } = Grid;

const QuestionWrapper = ({
  title,
  description,
  children,
  infoContentId,
  currentStep,
  setCurrentStep,
  hideSkip,
  inlineView = false,
}) => {
  const bp = useBreakpoint();
  const [showDrawer, setShowDrawer] = useState(false);

  const onNext = () => {
    setCurrentStep(currentStep + 1);
  };

  const onPrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  if (inlineView) {
    return (
      <div className="mb-8 p-6 bg-white rounded-lg shadow">
        <Row align="top" justify="space-between" className="pb-4">
          <Col>
            <Typography.Title level={4} className="text-gray-800">
              {title}
            </Typography.Title>
          </Col>
          <Col>
            {infoContentId && (
              <Tag
                color="blue"
                className="cursor-pointer rounded-full"
                onClick={() => setShowDrawer(true)}
              >
                More Info
              </Tag>
            )}
          </Col>
        </Row>

        <Typography.Paragraph
          className="text-gray-700"
          style={{
            fontFamily: "SF Pro Display, sans-serif",
            fontSize: "0.95rem",
          }}
        >
          {description}
        </Typography.Paragraph>
        <Divider className="my-4" />
        <div>{children}</div>
        {/* Mobile Drawer */}
        {!bp.xl && infoContentId && (
          <Definition
            contentId={infoContentId}
            onClose={() => setShowDrawer(false)}
            useDrawer
            visible={showDrawer}
          />
        )}
      </div>
    );
  }

  return (
    <div className="h-full">
      <Row wrap={false} className="h-full">
        {/* Main Content Area with its own contained buttons */}
        <Col flex="auto" className="relative">
          <div className="h-full bg-white">
            {/* Content with padding to prevent button overlap */}
            <div className="relative h-full flex flex-col">
              {/* Header Section */}
              <div className="p-8 xl:px-12">
                <Row align="top" justify="start" className={"px-8 py-4"}>
                  <div
                    className="text-gray-800 text-4xl"
                    style={{ fontFamily: "SF Pro Display, sans-serif" }}
                  >
                    {title}
                  </div>
                  {infoContentId && !bp.xl && (
                    <Tag
                      color="blue"
                      className="ml-4 cursor-pointer rounded-full"
                      onClick={() => setShowDrawer(true)}
                    >
                      More Info
                    </Tag>
                  )}
                </Row>
                <Typography.Paragraph
                  className="text-gray-700 px-8 pt-2"
                  style={{
                    fontFamily: "SF Pro Display, sans-serif",
                    fontSize: "0.95rem",
                  }}
                  ellipsis={{ rows: 2 }}
                >
                  {description}
                </Typography.Paragraph>
              </div>

              <Divider className="m-0" />

              {/* Content Section with Navigation */}
              <div className="flex-1 relative min-h-0">
                {/* Navigation Buttons */}
                <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10">
                  {currentStep > 0 && (
                    <button
                      onClick={onPrevious}
                      className="flex items-center justify-center w-12 h-12 ml-6 text-gray-600 bg-white rounded-full shadow-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label="Previous step"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                  )}
                </div>

                <div className="absolute right-0 top-1/2 -translate-y-1/2 z-10">
                  {!hideSkip && (
                    <button
                      onClick={onNext}
                      className="flex items-center justify-center w-12 h-12 mr-6 text-gray-600 bg-white rounded-full shadow-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label="Next step"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  )}
                </div>

                {/* Actual Content with padding for button space */}
                <div className="p-8 xl:px-12 mx-16 h-full overflow-auto">
                  {children}
                </div>
              </div>
            </div>
          </div>
        </Col>

        {/* Side Info Panel - Always rendered on xl but conditionally shows content */}
        {bp.xl && (
          <Col
            className={`px-8 pt-8 hide-scrollbar`}
            flex="0 0 400px"
            style={{
              height: "100%",
              overflowY: "auto",
              maxHeight: "90vh",
            }}
          >
            {infoContentId && <Definition contentId={infoContentId} />}
          </Col>
        )}

        {/* Mobile Drawer */}
        {!bp.xl && infoContentId && (
          <Definition
            contentId={infoContentId}
            onClose={() => setShowDrawer(false)}
            useDrawer
            visible={showDrawer}
          />
        )}
      </Row>
    </div>
  );
};

export default QuestionWrapper;